import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { ScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import Watermark from "@/components/base/Watermark";
import { BRAND_NAME, BRAND_TAGLINE, CATALOG_URL, SONGBO_URL, LOGO_URL } from "@/mocks/site-config";

const principles = [
  {
    num: "01",
    title: "知識整理",
    description: "將複雜內容整理成清楚的閱讀結構，透過系統化的章節安排與概念圖解，讓專業知識變得可被理解、可被查閱。",
  },
  {
    num: "02",
    title: "專業轉譯",
    description: "讓研究與專業觀點被更多讀者理解，協助作者將學術語言轉化為一般讀者也能掌握的內容，同時保持專業準確性。",
  },
  {
    num: "03",
    title: "長期閱讀",
    description: "出版值得反覆查閱與持續理解的內容，選擇具有長遠價值的知識主題，讓每一本書都能成為讀者的長期知識資產。",
  },
];

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main className="relative">
        <Watermark />
        {/* Hero */}
        <section className="min-h-[100svh] flex items-center bg-background-50 pt-16 md:pt-[68px]">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="flex flex-col lg:flex-row lg:gap-20 lg:items-start">
                <div className="lg:w-[35%] flex-shrink-0 mb-10 lg:mb-0 flex flex-col items-center text-center -mt-7 relative z-[2]">
                  <img
                    src={LOGO_URL}
                    alt="崧燁文化事業有限公司 Sunya"
                    className="w-[220px] md:w-[260px] h-auto object-contain mb-3 relative z-[2] bg-background-50"
                  />
                  <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium">
                    {BRAND_TAGLINE}
                  </p>
                </div>
                <div className="lg:w-[65%]">
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-[2.5rem] font-bold text-foreground-900 leading-tight mb-6">
                    關於{BRAND_NAME}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl">
                    崧燁文化事業有限公司專注人文社會、教育研究、歷史文化、醫療保健與專業知識出版，透過清楚的內容整理與閱讀分類，讓研究成果、專業觀點與實用知識走進更多人的閱讀生活。
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Brand Positioning */}
        <section className="min-h-[100svh] flex items-center bg-background-100 relative z-[2]">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="flex flex-col lg:flex-row lg:gap-16">
                <div className="lg:w-[30%] flex-shrink-0 mb-6 lg:mb-0">
                  <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium">
                    Positioning
                  </p>
                </div>
                <div className="lg:w-[70%] max-w-[620px]">
                  <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-6">品牌定位</h2>
                  <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-5">
                    <p>崧燁文化事業有限公司為崧博出版合作經銷品牌之一，核心定位為「讓專業知識成為可以持續閱讀的內容」。我們相信，專業知識不應該只停留在學術期刊與研究報告中，而應該經過整理與轉譯，成為一般讀者也可以理解、閱讀與使用的出版內容。</p>
                    <p>從人文社會到醫療保健，從教育研究到專業知識，崧燁文化事業有限公司選擇具有長遠價值的知識主題，以清楚的內容結構與可讀性高的編輯呈現，讓每一本書都能成為讀者的長期知識資源。</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Brand Principles */}
        <section className="min-h-[100svh] flex items-center bg-background-50">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="flex flex-col lg:flex-row lg:gap-16 mb-14 md:mb-20">
                <div className="lg:w-[30%] flex-shrink-0 mb-6 lg:mb-0">
                  <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium">
                    Principles
                  </p>
                </div>
                <div className="lg:w-[70%]">
                  <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                    品牌核心
                  </h2>
                </div>
              </div>
            </ScrollReveal>

            <div className="lg:ml-[30%] lg:pl-16 space-y-14 md:space-y-20">
              {principles.map((p, i) => (
                <ScrollReveal key={p.num} delay={i * 120}>
                  <div className={`flex flex-col ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-6 lg:gap-12 items-start`}>
                    <div className="flex-shrink-0">
                      <span className="font-heading text-5xl md:text-6xl font-bold text-background-300 select-none leading-none">
                        {p.num}
                      </span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3">
                        {p.title}
                      </h3>
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-md">
                        {p.description}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Relation with Songbo */}
        <section className="min-h-[80svh] flex items-center bg-background-100 relative z-[2]">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="flex flex-col lg:flex-row lg:gap-16">
                <div className="lg:w-[30%] flex-shrink-0 mb-6 lg:mb-0">
                  <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium">
                    Relation
                  </p>
                </div>
                <div className="lg:w-[70%] max-w-[620px]">
                  <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-6">與崧博出版的關係</h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                    崧燁文化事業有限公司為崧博出版合作經銷品牌之一，主要聚焦人文社會、教育研究、歷史文化、醫療保健與專業知識內容。完整出版服務由崧博出版主站說明，正式書目則可前往崧博出版書籍資料庫查詢。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href={SONGBO_URL} target="_blank" rel="nofollow noopener noreferrer" className="bg-foreground-900 hover:bg-foreground-800 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground-400 focus-visible:ring-offset-2">
                      認識崧博出版 <i className="ri-external-link-line text-sm"></i>
                    </a>
                    <a href={CATALOG_URL} target="_blank" rel="nofollow noopener noreferrer" className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2">
                      前往書籍資料庫 <i className="ri-external-link-line text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Store CTA */}
        <section className="min-h-[40svh] flex items-center bg-background-50 border-t border-background-200/60">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-20 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="lg:ml-[30%] lg:pl-16">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-8 h-px bg-foreground-300"></span>
                  <span className="text-xs text-foreground-400 tracking-wider">開始探索</span>
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  開始探索崧燁文化事業有限公司的出版內容
                </h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/stores" className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2">
                    合作通路 <i className="ri-store-2-line text-sm"></i>
                  </Link>
                  <Link to="/faq" className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2">
                    常見問題 <i className="ri-question-line text-sm"></i>
                  </Link>
                  <a href={CATALOG_URL} target="_blank" rel="nofollow noopener noreferrer" className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400 focus-visible:ring-offset-2">
                    書籍資料庫 <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}